import { useCallback, useMemo, useState } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { mdiArrowRight, mdiInformationOutline, mdiAlertDecagram } from '@mdi/js'
import { FormattedMessage, useIntl } from 'react-intl'
import { DateTime } from 'luxon'
import Icon from '@mdi/react'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import theme from 'theme_'
import routes from 'app/routes'
import CommonLinkButton from 'components/Button/CommonLinkButton'
import { useHomeRouteParams } from 'features/home/hooks'
import { useAppSelector } from 'app/hooks'
import {
  userSubscriptionPlanSelector,
  activeSubscriptionSelector,
  renewSubscriptionSelector,
  selectedHomeSelector,
} from 'features/home/selectors'
import SubscriptionIcon from 'components/Icons/SubscriptionIcon'
import { SectionLabel } from 'components/Typography'
import { SpinnerCentered } from 'components/Loader'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import CancelEssentialsModal from './CancelEssentialsModal'
import {
  getSubscriptionPlanName,
  getHomeSubscriptonInfo,
} from 'features/home/utils'
import { hasSubscriptionPermissionSelector } from 'features/auth/permissions/selectors'
import Tooltip from 'components/Tooltip'
import { CommonLink } from 'components/Button'
import { subscriptionInfoMessages } from '../../../HomeSubscription/messages'
import { isExpiringSoon } from 'features/home/components/HomeSubscription/utils'

import { StyledIcon, StyledIcoBasic, SubInfoBlock, SubInfoBody } from './styles'

import {
  SubscriptionInfoPlanBlock,
  SubscriptionInfoPlanInfo,
  SubscriptionInfoStyledManageButton,
  SubscriptionInfoBlock,
  SubscriptionInfoManagedByLabel,
  SubsInfoBottomSection,
  SubsInfoCancelSubscription,
  SubsInfoBottomSectionLeftBlock,
  SubsInfoTooltipWrapper,
  SubscriptionInfoManagedSubLabel,
} from './styles'

const SUBSCRIPTION_STATE = {
  NONE: 'NONE',
  ENROLLED: 'ENROLLED',
  WILL_NOT_RENEW: 'WILL_NOT_RENEW',
  EXPIRES: 'EXPIRES',
}

export interface SubscriptionInfoParams {
  // TODO refactor below flags
  // the component shouldn't hold any information about the places where it's being used
  isHomeDashboard: boolean
  isDetailsPage?: boolean
  hasShowMore?: boolean
  priceId: string
  paymentMethodId: string
  renewingStatus: number[]
}

function SubscriptionInfo(props: SubscriptionInfoParams) {
  const { homeId } = useHomeRouteParams()
  const { formatMessage } = useIntl()
  const history = useHistory()

  const [subsCancelOpen, setSubsCancelOpen] = useState(false)

  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const isFetching = useAppSelector(activeSubscriptionSelector.isFetching)
  const plan = useAppSelector(userSubscriptionPlanSelector)

  const isRenewing = useAppSelector(renewSubscriptionSelector.isFetching)
  const home = useAppSelector(selectedHomeSelector.data)
  const subscriptionInfo = getHomeSubscriptonInfo(home)
  const hasSubscriptionPermission = useAppSelector(
    hasSubscriptionPermissionSelector
  )

  const subscriptionState = useMemo(() => {
    if (subscriptionInfo) {
      if (subscriptionInfo.autoRenew) return SUBSCRIPTION_STATE.ENROLLED
      return isExpiringSoon(subscriptionInfo, DateTime.now())
        ? SUBSCRIPTION_STATE.EXPIRES
        : SUBSCRIPTION_STATE.WILL_NOT_RENEW
    }
    return SUBSCRIPTION_STATE.NONE
  }, [subscriptionInfo])

  const subscriptionEnabled = useMemo(() => {
    return !!home?.hostInfo?.hostCapabilities?.reduce((a, c) => {
      return a || (c.type === 'cloudLicenses' && c.version >= 2)
    }, false)
  }, [home])

  const subscriptionPath = generatePath(routes.homeSubscription.root(), {
    homeId,
    rootDomainId: currentRootId || '',
  })

  const openUpgradeSubscriptionPage = useCallback(() => {
    if (currentRootId) {
      const subscriptionUpgradePath = generatePath(
        routes.homeSubscription.upgrade(),
        {
          homeId,
          rootDomainId: currentRootId,
        }
      )
      history.push(subscriptionUpgradePath)
    }
  }, [currentRootId, history, homeId])

  const openSubscriptionDetailsPage = useCallback(() => {
    if (currentRootId) {
      const subscriptionDetailsPath = generatePath(
        routes.homeSubscription.renewals(),
        {
          homeId,
          rootDomainId: currentRootId,
        }
      )
      history.push(subscriptionDetailsPath)
    }
  }, [currentRootId, history, homeId])

  const openUpdateSubscriptionPage = useCallback(() => {
    if (currentRootId) {
      const subscriptionUpdatePath = generatePath(
        routes.homeSubscription.upgrade(),
        {
          homeId,
          rootDomainId: currentRootId,
        }
      )
      history.push(subscriptionUpdatePath)
    }
  }, [currentRootId, history, homeId])

  const handleCancelSubscription = () => {
    setSubsCancelOpen(true)
  }
  const planName = getSubscriptionPlanName(plan, formatMessage)

  const subscriptionTitle = useMemo(() => {
    if (subscriptionState === SUBSCRIPTION_STATE.ENROLLED) {
      return formatMessage(subscriptionInfoMessages.enrolledPlan, { planName })
    }
    if (subscriptionState === SUBSCRIPTION_STATE.EXPIRES) {
      return formatMessage(subscriptionInfoMessages.expiringPlan, { planName })
    }
    if (subscriptionState === SUBSCRIPTION_STATE.WILL_NOT_RENEW) {
      return formatMessage(subscriptionInfoMessages.nonRenewalPlan, {
        planName: formatMessage(subscriptionInfoMessages.essentialPlanName),
      })
    }
    return formatMessage(subscriptionInfoMessages.subscriptionUpgrade)
  }, [subscriptionState, planName, formatMessage])

  return (
    <SubInfoBlock $isChild={props.isDetailsPage}>
      {subsCancelOpen && (
        <CancelEssentialsModal
          onClose={() => setSubsCancelOpen(false)}
          homeId={homeId}
          periodEnd={subscriptionInfo?.expiresAt}
          priceId={props.priceId}
          paymentMethodId={props.paymentMethodId}
        />
      )}
      {isFetching || isRenewing ? (
        <SpinnerCentered />
      ) : (
        <>
          <SectionLabel>
            <span>
              <FormattedMessage
                {...subscriptionInfoMessages.essentialPlanName}
              />
            </span>
            {props.isHomeDashboard && (
              <CommonLink to={subscriptionPath}>
                <StyledIcon path={mdiArrowRight} size={1} />
              </CommonLink>
            )}
            {props.hasShowMore && !props.isHomeDashboard && (
              <CommonLinkButton onClick={openSubscriptionDetailsPage}>
                <FormattedMessage {...subscriptionInfoMessages.moreDetails} />
              </CommonLinkButton>
            )}
          </SectionLabel>

          <SubInfoBody>
            <SubscriptionInfoBlock>
              <SubscriptionInfoPlanInfo>
                <SubscriptionInfoPlanBlock>
                  {subscriptionInfo ? (
                    <SubscriptionIcon subscriptionInfo={subscriptionInfo} />
                  ) : (
                    <StyledIcoBasic path={mdiAlertDecagram} size={1} />
                  )}
                  <SubscriptionInfoManagedByLabel>
                    {subscriptionTitle}
                  </SubscriptionInfoManagedByLabel>
                </SubscriptionInfoPlanBlock>
                {([
                  SUBSCRIPTION_STATE.EXPIRES,
                  SUBSCRIPTION_STATE.WILL_NOT_RENEW,
                ].includes(subscriptionState) ||
                  props.renewingStatus[1] === 0) && (
                  <SubscriptionInfoManagedSubLabel>
                    Expires on{' '}
                    <IntlFormattedDate date={subscriptionInfo?.expiresAt} />
                  </SubscriptionInfoManagedSubLabel>
                )}
              </SubscriptionInfoPlanInfo>

              <SubsInfoBottomSection>
                <SubsInfoBottomSectionLeftBlock>
                  {subscriptionState === SUBSCRIPTION_STATE.ENROLLED &&
                    props.renewingStatus[1] === 1 && (
                      <>
                        {!props.isDetailsPage && (
                          <SubscriptionInfoStyledManageButton
                            type="button"
                            variant={
                              subscriptionState !== SUBSCRIPTION_STATE.ENROLLED
                                ? 'primary'
                                : 'secondary'
                            }
                            onClick={openSubscriptionDetailsPage}
                            disabled={
                              !hasSubscriptionPermission
                            }
                          >
                            {formatMessage(subscriptionInfoMessages.manage)}
                          </SubscriptionInfoStyledManageButton>
                        )}

                        {props.isDetailsPage && (
                          <SubsInfoCancelSubscription
                            as="button"
                            variant={'secondary'}
                            disabled={!hasSubscriptionPermission}
                            onClick={handleCancelSubscription}
                          >
                            <FormattedMessage
                              {...subscriptionInfoMessages.cancelRenewal}
                            />
                          </SubsInfoCancelSubscription>
                        )}
                      </>
                    )}

                  {([
                    SUBSCRIPTION_STATE.EXPIRES,
                    SUBSCRIPTION_STATE.WILL_NOT_RENEW,
                  ].includes(subscriptionState) ||
                    props.renewingStatus[1] === 0) && (
                    <SubscriptionInfoStyledManageButton
                      type="button"
                      variant={
                        subscriptionState !== SUBSCRIPTION_STATE.ENROLLED
                          ? 'primary'
                          : 'secondary'
                      }
                      disabled={
                        !hasSubscriptionPermission || !subscriptionEnabled
                      }
                      onClick={openUpdateSubscriptionPage}
                    >
                      {formatMessage(subscriptionInfoMessages.resubscribe)}
                    </SubscriptionInfoStyledManageButton>
                  )}

                  {subscriptionState === SUBSCRIPTION_STATE.NONE &&
                    subscriptionEnabled && (
                      <SubscriptionInfoStyledManageButton
                        type="button"
                        variant="primary"
                        disabled={
                          !hasSubscriptionPermission
                        }
                        onClick={openUpgradeSubscriptionPage}
                      >
                        {formatMessage(subscriptionInfoMessages.upgrade)}
                      </SubscriptionInfoStyledManageButton>
                    )}

                  {hasSubscriptionPermission && !subscriptionEnabled && (
                    <SubsInfoTooltipWrapper>
                      <Tooltip
                        text={formatMessage(
                          subscriptionInfoMessages.noValidVersion
                        )}
                        showOn="click"
                      >
                        <Icon
                          path={mdiInformationOutline}
                          size={1}
                          color={theme.colors.horizonOrange}
                        />
                      </Tooltip>
                    </SubsInfoTooltipWrapper>
                  )}

                  {!hasSubscriptionPermission && (
                    <SubsInfoTooltipWrapper>
                      <Tooltip
                        text={formatMessage(
                          subscriptionInfoMessages.noPurchasePermission
                        )}
                        showOn="click"
                      >
                        <Icon
                          path={mdiInformationOutline}
                          size={1}
                          color={theme.colors.horizonOrange}
                        />
                      </Tooltip>
                    </SubsInfoTooltipWrapper>
                  )}
                </SubsInfoBottomSectionLeftBlock>
              </SubsInfoBottomSection>
            </SubscriptionInfoBlock>
          </SubInfoBody>
        </>
      )}
    </SubInfoBlock>
  )
}

export default SubscriptionInfo
